import React from "react";
import {
     Box,
     Chip,
     DialogContentText,
     IconButton,
     Tooltip,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";

interface ConciergeCommunitiesListProps {
     conciergeCommunities: Array<{ community: string; assigned: number; available: number }>;
     selectedBulkConcierge: { name: string; email: string } | null;
     userInfo: any;
     isBulkAssigning: boolean;
     onReassignClick: (communityName: string) => void;
     onUnassignClick: (communityName: string) => void;
}

export const ConciergeCommunitiesList: React.FC<ConciergeCommunitiesListProps> = ({
     conciergeCommunities,
     selectedBulkConcierge,
     userInfo,
     isBulkAssigning,
     onReassignClick,
     onUnassignClick,
}) => {
     if (!selectedBulkConcierge || conciergeCommunities.length === 0) return null;

     return (
          <Box sx={{ marginTop: "16px", marginBottom: "8px" }}>
               <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: "8px" }}>
                    <DialogContentText style={{ fontSize: "13px", color: "#666", fontWeight: "500" }}>
                         {selectedBulkConcierge.email === userInfo?.email ? (
                              <>
                                   <span style={{ color: "#1976d2", fontWeight: "600" }}>My assigned communities</span>
                                   <span style={{
                                        marginLeft: "8px",
                                        fontSize: "12px",
                                        backgroundColor: "#1976d2",
                                        color: "white",
                                        padding: "2px 6px",
                                        borderRadius: "10px",
                                        fontWeight: "600"
                                   }}>
                                        You
                                   </span>
                              </>
                         ) : (
                              <>
                                   {selectedBulkConcierge.name} Currently assigned to:
                              </>
                         )}
                    </DialogContentText>
               </Box>
               {/* Horizontal divider bar */}
               <Box
                    sx={{
                         width: "100%",
                         height: "2px",
                         backgroundColor: "#e0e0e0",
                         marginBottom: "12px",
                         borderRadius: "1px"
                    }}
               />
               {/* Icon Legend */}
               <Box sx={{ marginBottom: "12px", padding: "8px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "nowrap" }}>
                         <DialogContentText
                              component="span"
                              style={{
                                   fontSize: "12px",
                                   color: "#666",
                                   fontWeight: "500",
                                   whiteSpace: "nowrap"
                              }}
                         >
                              Icon Legend:
                         </DialogContentText>
                         <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
                              <IconButton
                                   size="small"
                                   disabled
                                   sx={{
                                        color: "#2e7d32",
                                        padding: "4px",
                                        cursor: "default",
                                        "&.Mui-disabled": {
                                             color: "#2e7d32",
                                        },
                                        "& svg": {
                                             fontSize: "16px",
                                             color: "#2e7d32",
                                        },
                                   }}
                              >
                                   <AssignmentIcon fontSize="small" />
                              </IconButton>
                              <DialogContentText component="span" style={{ fontSize: "12px", color: "#333", whiteSpace: "nowrap" }}>
                                   Reassign to another concierge
                              </DialogContentText>
                         </Box>
                         <Box
                              sx={{
                                   color: "#666",
                                   fontSize: "12px",
                                   padding: "0 8px",
                                   display: "flex",
                                   alignItems: "center",
                              }}
                         >
                              |
                         </Box>
                         <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
                              <IconButton
                                   size="small"
                                   disabled
                                   sx={{
                                        color: "#d32f2f",
                                        padding: "4px",
                                        cursor: "default",
                                        "&.Mui-disabled": {
                                             color: "#d32f2f",
                                        },
                                        "& svg": {
                                             fontSize: "16px",
                                             color: "#d32f2f",
                                        },
                                   }}
                              >
                                   <AssignmentLateIcon fontSize="small" />
                              </IconButton>
                              <DialogContentText component="span" style={{ fontSize: "12px", color: "#333", whiteSpace: "nowrap" }}>
                                   Unassign concierge from community
                              </DialogContentText>
                         </Box>
                    </Box>
               </Box>
               <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {conciergeCommunities.map(({ community, assigned, available }) => (
                         <Box
                              key={community}
                              sx={{
                                   display: "inline-flex",
                                   alignItems: "center",
                                   gap: 0.5,
                                   backgroundColor: "#e3f2fd",
                                   borderRadius: "16px",
                                   padding: "4px 8px",
                                   "&:hover": {
                                        backgroundColor: "#bbdefb",
                                   },
                              }}
                         >
                              <Tooltip title="Reassign to another concierge">
                                   <IconButton
                                        size="small"
                                        onClick={() => onReassignClick(community)}
                                        disabled={isBulkAssigning}
                                        sx={{
                                             color: "#2e7d32",
                                             padding: "4px",
                                             "& svg": {
                                                  fontSize: "16px",
                                             },
                                             "&:hover": {
                                                  backgroundColor: "rgba(46, 125, 50, 0.1)",
                                                  color: "#1b5e20",
                                             },
                                        }}
                                   >
                                        <AssignmentIcon fontSize="small" />
                                   </IconButton>
                              </Tooltip>
                              <Tooltip title="Unassign concierge from community">
                                   <IconButton
                                        size="small"
                                        onClick={() => onUnassignClick(community)}
                                        disabled={isBulkAssigning}
                                        sx={{
                                             color: "#d32f2f",
                                             padding: "4px",
                                             "& svg": {
                                                  fontSize: "16px",
                                             },
                                             "&:hover": {
                                                  backgroundColor: "rgba(211, 47, 47, 0.1)",
                                                  color: "#b71c1c",
                                             },
                                        }}
                                   >
                                        <AssignmentLateIcon fontSize="small" />
                                   </IconButton>
                              </Tooltip>
                              <Box
                                   sx={{
                                        color: "#1976d2",
                                        fontSize: "14px",
                                        padding: "0 4px",
                                        display: "flex",
                                        alignItems: "center",
                                   }}
                              >
                                   |
                              </Box>
                              <Chip
                                   label={`${community} (${assigned} assigned, ${available} available)`}
                                   size="small"
                                   disabled={isBulkAssigning}
                                   sx={{
                                        backgroundColor: "transparent",
                                        color: "#1976d2",
                                        fontWeight: "500",
                                   }}
                              />
                         </Box>
                    ))}
               </Box>
          </Box>
     );
};
